import { useEffect, useMemo, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WorkPage } from '@web/components/work-page.tsx';
import { usePlanFeature } from '@web/components/plan-gate.tsx';
import { useSession } from '@web/state/session.tsx';
import { useDocumentTitle } from '@web/hooks/index.ts';
import '../reports.css';
import {
  ReportTimezone,
  defaultRangePreset,
  isSection,
  resolveReportRange,
  visibleReportSections,
  type Section,
} from './shared.tsx';
import { ReportRangeControls, ReportScopeToggle } from './range-controls.tsx';
import { ReportSectionBody } from './report-body.tsx';
import { useReportPreferences } from './preferences.ts';

/**
 * The frame every single report opens in: the way back to the hub, the
 * report's title and what it answers, the locations toggle, the range
 * controls for this report alone, and the report.
 */
export function ReportShell({
  title,
  description,
  actions,
  controls,
  children,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  controls?: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Link to="/reports" className="report-back-link">
        {t('reports.hub.backToHub')}
      </Link>
      <WorkPage title={title} description={description} actions={actions}>
        {controls}
        {children}
      </WorkPage>
    </>
  );
}

/**
 * `/reports/:section`. A report this company cannot open — an unknown name,
 * or Job costing for a residential company — goes back to the hub. With the
 * hub flag off the same link opens that tab of the tabbed page, so a link
 * shared from a company that has the hub still lands on the right report.
 */
export default function ReportSectionPage() {
  const { section } = useParams();
  const location = useLocation();
  const { company } = useSession();
  const hasReports = usePlanFeature('reports_core');
  const requested = section ?? null;

  if (company?.reportsHubRoutes !== true) {
    const search = new URLSearchParams(location.search);
    if (isSection(requested)) search.set('section', requested);
    const query = search.toString();
    return (
      <Navigate
        replace
        to={{ pathname: '/reports', search: query ? `?${query}` : '', hash: location.hash }}
        state={location.state}
      />
    );
  }
  if (!isSection(requested) || !visibleReportSections(company, hasReports).includes(requested)) {
    return <Navigate replace to="/reports" />;
  }
  return <ReportSectionView section={requested} />;
}

function ReportSectionView({ section }: { section: Section }) {
  const { t } = useTranslation();
  const title = t(`reports.tab.${section}`);
  useDocumentTitle(title);
  const { company, currentLocation, locations, user } = useSession();
  const [preferences, setPreferences] = useReportPreferences();

  // The hub's "pick up where you left off" link.
  useEffect(() => {
    setPreferences({ lastSection: section });
  }, [section, setPreferences]);

  const scope = locations.length >= 2 && preferences.scope === 'all' ? ('all' as const) : undefined;
  const preset = preferences.preset ?? defaultRangePreset(section);
  const timezone = scope === 'all' ? company?.timezone : (currentLocation?.timezone ?? company?.timezone);
  const range = useMemo(
    () => resolveReportRange(preset, preferences.customFrom, preferences.customTo, timezone),
    [preset, preferences.customFrom, preferences.customTo, timezone],
  );
  // A different company, person or location starts the report over: its
  // filters and paging belonged to the one before.
  const identity = JSON.stringify([company?.id, user?.id, currentLocation?.id]);

  return (
    <ReportTimezone.Provider value={timezone ?? 'UTC'}>
      <ReportShell
        title={title}
        description={t(`reports.hub.descriptions.${section}`)}
        actions={
          <ReportScopeToggle choice={preferences.scope} onChange={(choice) => setPreferences({ scope: choice })} />
        }
        controls={
          <ReportRangeControls
            section={section}
            preset={preset}
            onPreset={(value) => setPreferences({ preset: value })}
            customFrom={preferences.customFrom}
            customTo={preferences.customTo}
            onCustomFrom={(value) => setPreferences({ customFrom: value })}
            onCustomTo={(value) => setPreferences({ customTo: value })}
          />
        }
      >
        <ReportSectionBody key={`${identity}:${section}`} section={section} range={range} scope={scope} />
      </ReportShell>
    </ReportTimezone.Provider>
  );
}
